import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { useMemo, useRef, useState } from "react";

import { extractMarkdownHeadings } from "./markdown_headings";
import { SafeMarkdown } from "./safe_markdown";
import type { HelpArticle } from "./types";

type HelpArticleViewProps = {
  article: HelpArticle | null;
  loading: boolean;
  error?: string;
  onBack: () => void;
  onRetry?: () => void;
  onRefreshMedia?: () => void;
  canDelete?: boolean;
  onDelete?: () => Promise<unknown>;
};

const formatUpdatedAt = (updatedAt: string) =>
  new Intl.DateTimeFormat(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(new Date(updatedAt));

export const HelpArticleView = ({
  article,
  loading,
  error,
  onBack,
  onRetry,
  onRefreshMedia,
  canDelete = false,
  onDelete,
}: HelpArticleViewProps) => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const headings = useMemo(
    () => (article ? extractMarkdownHeadings(article.markdown) : []),
    [article],
  );
  const mediaRefreshAttempted = useRef(false);
  const mediaGeneration = article
    ? JSON.stringify([
        article.slug,
        article.assets.map(({ filename, url }) => [filename, url]),
      ])
    : null;
  const currentMediaGeneration = useRef<string | null>(null);
  if (currentMediaGeneration.current !== mediaGeneration) {
    currentMediaGeneration.current = mediaGeneration;
    mediaRefreshAttempted.current = false;
  }
  const refreshMediaOnce = () => {
    if (mediaRefreshAttempted.current || !onRefreshMedia) return;
    mediaRefreshAttempted.current = true;
    onRefreshMedia();
  };
  const deleteArticle = async () => {
    if (!onDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await onDelete();
      setDeleteConfirmOpen(false);
    } catch (error) {
      setDeleteError(
        error instanceof Error
          ? error.message
          : "The article could not be deleted. Try again.",
      );
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box
        aria-label="Loading Help article"
        sx={{ flex: 1, overflowY: "auto", p: { xs: 2, sm: 4 } }}
      >
        <Box sx={{ maxWidth: 880, mx: "auto" }}>
          <Skeleton width={140} height={38} />
          <Skeleton width="35%" height={28} sx={{ mt: 4 }} />
          <Skeleton width="80%" height={64} />
          <Skeleton width="60%" height={30} />
          <Skeleton variant="rounded" height={360} sx={{ mt: 4 }} />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ flex: 1, overflowY: "auto", p: { xs: 2, sm: 4 } }}>
        <Box sx={{ maxWidth: 760, mx: "auto" }}>
          <Button startIcon={<ArrowBackRoundedIcon />} onClick={onBack}>
            Back to Help center
          </Button>
          <Alert
            severity="error"
            sx={{ mt: 3 }}
            action={
              onRetry ? (
                <Button
                  color="inherit"
                  size="small"
                  startIcon={<RefreshRoundedIcon />}
                  onClick={onRetry}
                >
                  Try again
                </Button>
              ) : undefined
            }
          >
            {error}
          </Alert>
        </Box>
      </Box>
    );
  }

  if (!article) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "grid",
          placeItems: "center",
          p: 3,
          bgcolor: "atomic.surfaceTint",
        }}
      >
        <Box sx={{ textAlign: "center", maxWidth: 440 }}>
          <ArticleOutlinedIcon sx={{ fontSize: 52, color: "atomic.faint" }} />
          <Typography variant="h5" fontWeight={700} mt={1.5}>
            This article is not available
          </Typography>
          <Typography color="text.secondary" mt={1}>
            It may have moved, or it may not be published for this customer.
          </Typography>
          <Button variant="contained" onClick={onBack} sx={{ mt: 2.5 }}>
            Browse Help articles
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      data-testid="help-article"
      sx={{
        flex: 1,
        overflowY: "auto",
        bgcolor: "atomic.surfaceTint",
        px: { xs: 2, sm: 3, lg: 5 },
        py: { xs: 2, sm: 3 },
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto" }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          mb={2}
        >
          <Button
            startIcon={<ArrowBackRoundedIcon />}
            onClick={onBack}
            sx={{ color: "text.secondary" }}
          >
            Back to Help center
          </Button>
          {canDelete && onDelete && (
            <Button
              color="error"
              startIcon={<DeleteOutlineRoundedIcon />}
              onClick={() => {
                setDeleteError(null);
                setDeleteConfirmOpen(true);
              }}
            >
              Delete article
            </Button>
          )}
        </Stack>

        <Paper
          variant="outlined"
          sx={{
            borderColor: "atomic.border",
            borderRadius: { xs: 3, sm: 4 },
            overflow: "hidden",
          }}
        >
          <Box
            component="header"
            sx={{
              px: { xs: 2.5, sm: 5, lg: 7 },
              py: { xs: 3.5, sm: 5 },
              background:
                "linear-gradient(145deg, #F7FAFF 0%, #EDF3FF 55%, #F7F9FC 100%)",
              borderBottom: "1px solid",
              borderColor: "atomic.borderLight",
            }}
          >
            <Chip
              label={article.category}
              size="small"
              sx={{
                bgcolor: "atomic.blue1",
                color: "atomic.brandBlueDark",
                fontWeight: 700,
              }}
            />
            <Typography
              component="h1"
              sx={{
                mt: 2,
                maxWidth: 820,
                fontSize: { xs: 30, sm: 42 },
                lineHeight: 1.12,
                letterSpacing: "-0.025em",
                fontWeight: 750,
                color: "atomic.ink",
              }}
            >
              {article.title}
            </Typography>
            <Typography
              sx={{
                mt: 1.5,
                maxWidth: 720,
                fontSize: { xs: 16, sm: 18 },
                lineHeight: 1.6,
                color: "atomic.inkSecondary",
              }}
            >
              {article.summary}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mt={2.5}
            >
              Updated {formatUpdatedAt(article.updatedAt)}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                md: headings.length ? "minmax(0, 1fr) 230px" : "minmax(0, 1fr)",
              },
              gap: { md: 5 },
              px: { xs: 2.5, sm: 5, lg: 7 },
              py: { xs: 3.5, sm: 5 },
            }}
          >
            {headings.length > 0 && (
              <Box
                component="aside"
                sx={{
                  gridColumn: { xs: 1, md: 2 },
                  gridRow: 1,
                  p: { xs: 2, md: 0 },
                  border: { xs: "1px solid", md: 0 },
                  borderColor: { xs: "atomic.border", md: "transparent" },
                  borderRadius: { xs: 2, md: 0 },
                  bgcolor: { xs: "atomic.surfaceTint", md: "transparent" },
                }}
              >
                <Box sx={{ position: "sticky", top: 16 }}>
                  <Typography
                    variant="overline"
                    fontWeight={800}
                    color="text.secondary"
                  >
                    On this page
                  </Typography>
                  <Divider sx={{ my: 1.25 }} />
                  <Stack alignItems="stretch" spacing={0.25}>
                    {headings.map((heading) => (
                      <Button
                        key={heading.id}
                        size="small"
                        color="inherit"
                        onClick={() => {
                          document.getElementById(heading.id)?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                          });
                        }}
                        sx={{
                          justifyContent: "flex-start",
                          pl: heading.depth === 3 ? 2 : 0.5,
                          textAlign: "left",
                          color: "text.secondary",
                          fontWeight: heading.depth === 2 ? 650 : 450,
                        }}
                      >
                        {heading.label}
                      </Button>
                    ))}
                  </Stack>
                </Box>
              </Box>
            )}
            <Box
              component="article"
              sx={{
                minWidth: 0,
                maxWidth: 760,
                gridColumn: 1,
                gridRow: { xs: headings.length ? 2 : 1, md: 1 },
              }}
            >
              <SafeMarkdown
                markdown={article.markdown}
                assets={article.assets}
                suppressFirstHeading
                onMediaError={refreshMediaOnce}
              />
            </Box>
          </Box>
        </Paper>

        <Dialog
          open={deleteConfirmOpen}
          onClose={deleting ? undefined : () => setDeleteConfirmOpen(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>Delete this article?</DialogTitle>
          <DialogContent>
            <Typography color="text.secondary">
              <strong>{article.title}</strong> will immediately disappear from
              this customer&apos;s Help center. Other customers are not
              affected.
            </Typography>
            {deleteError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {deleteError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button
              disabled={deleting}
              onClick={() => setDeleteConfirmOpen(false)}
            >
              Keep article
            </Button>
            <Button
              color="error"
              variant="contained"
              disabled={deleting}
              onClick={() => void deleteArticle()}
            >
              {deleting ? "Deleting..." : "Delete now"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};
